import { Message, MessageType, SseRecord } from '../models';
import { sseRecordResource } from '../resource';

export class HistoryService {
  /**
   * Clear the author cache (useful when switching conversations)
   */
  clearCache(): void {
    // No-op for now - kept for backward compatibility
  }

  /**
   * Get the most recent SseRecord ID for use as lastEventId
   */
  getLastEventId(messages: Message[]): string | undefined {
    if (messages.length === 0) {
      return undefined;
    }

    // Messages are ordered by date ASC, so last one is most recent
    const lastMessage = messages[messages.length - 1];

    // Extract the numeric ID from the IRI
    // e.g., "/api/sse_records/123" -> "123"
    const match = lastMessage['@id'].match(/\/(\d+)$/);
    return match ? match[1] : undefined;
  }

  /**
   * Loads conversation history from SseRecord database
   * @param conversationIri - The conversation IRI (e.g., "/api/conversations/123")
   * @returns Array of enriched Message objects ordered by date ASC
   */
  async loadHistory(conversationIri: string): Promise<Message[]> {
    try {
      // Extract numeric ID from IRI for the filter
      // API Platform SearchFilter on ManyToOne expects the numeric ID, not the IRI
      const idMatch = conversationIri.match(/\/(\d+)$/);
      const conversationId = idMatch ? idMatch[1] : conversationIri;

      // Fetch all SseRecords for this conversation, ordered by date ASC
      // Using a very high perPage limit instead of pagination: false to avoid JSON parse errors
      const sseRecordsCollection = await sseRecordResource.findAll({
        conversation: conversationId, // Use numeric ID for the filter
        'order[date]': 'asc',
        perPage: 10000,
        page: 1,
      });

      const sseRecords = sseRecordsCollection.members;

      if (!sseRecords || sseRecords.length === 0) {
        return [];
      }

      // Transform SseRecords to Messages
      // Note: We use authorType to determine message ownership, so we don't need to fetch author names
      const messages = sseRecords.map(record =>
        this.transformToMessage(record),
      );

      return messages;
    } catch (error) {
      console.error('Failed to load conversation history:', error);
      // Return empty array instead of throwing to prevent blocking the chat
      return [];
    }
  }

  /**
   * Transform SseRecord to Message format
   * Note: We don't set the author name here - ChatItem uses authorType to determine message ownership
   */
  private transformToMessage(record: SseRecord): Message {
    return {
      '@id': record['@id'],
      author: undefined, // Not needed - we use authorType instead
      authorIdentifier: record.authorIdentifier || undefined,
      authorType: record.authorType as any, // SseRecord uses string, Message uses AuthorType enum
      conversation: record.conversation['@id'],
      date: record.date,
      message: record.message,
      talismanUrl: record.talismanUrl || undefined,
      type: record.type as MessageType, // SseRecord uses string, Message uses MessageType enum
    };
  }
}

export const historyService = new HistoryService();
